<template>
    <div>
        <Toast />
        <Fieldset legend="ค้นหา" :toggleable="true">
            <div class="p-fluid p-mt-2 p-ml-3 p-mr-3 p-mb-2">
                <div class="p-formgrid p-grid p-col">
                    <div class="p-field p-col-2">
                        <label>เวลาเริ่มต้น</label>
                        <Calendar
                            v-model="startTime"
                            dateFormat="dd/mm/yy"
                            :maxDate="endTime"
                            :showTime="true"
                            :showButtonBar="true"
                            :monthNavigator="true"
                            :yearNavigator="true"
                            yearRange="2020:2050"
                        />
                    </div>
                    <div class="p-field p-col-2">
                        <label>เวลาสิ้นสุด</label>
                        <Calendar
                            v-model="endTime"
                            dateFormat="dd/mm/yy"
                            :minDate="startTime"
                            :showTime="true"
                            :showButtonBar="true"
                            :monthNavigator="true"
                            :yearNavigator="true"
                            yearRange="2020:2050"
                        />
                    </div>
                    <div class="p-field p-col-2">
                        <label>แหล่งที่มา</label>
                        <Dropdown v-model="source" :options="sourceOptions" optionLabel="label" optionValue="value" />
                    </div>
                    <div class="p-field p-col-3">
                        <label>Endpoint</label>
                        <Dropdown v-model="endpoint" :options="endpointOptions" optionLabel="label" optionValue="value" />
                    </div>
                    <div class="p-field p-col">
                        <div class="p-formgrid p-grid p-jc-end p-mt-4">
                            <div class="p-field p-col-12 p-mt-2">
                                <Button label="ส่งออก CSV" class="p-button-success p-mr-3" @click="$refs.detailTable.exportCSV()" />
                                <Button label="ล้าง" class="p-button-secondary p-mr-3" @click="clearFilters()" />
                                <Button label="ค้นหา" class="p-button-info" @click="loadLogs()" />
                            </div>
                        </div>
                    </div>
                </div>
                <small>
                    "ระบบภายนอก" คือ request ที่ไม่มี Origin หรือมี Origin ที่ไม่ใช่เว็บ MIS-GIS (เรียกจาก server/Postman/ระบบอื่น) ส่วน
                    "เว็บ MIS-GIS" คือ request ที่มาจากหน้าเว็บของเราเอง
                </small>
            </div>
        </Fieldset>

        <div class="card p-mt-2">
            <h5>สรุปรายวัน</h5>
            <DataTable :value="summary" :loading="loading" :rowHover="true" :paginator="true" :rows="10" dataKey="key">
                <template #empty>ไม่พบข้อมูล</template>
                <Column field="day" header="วันที่" :sortable="true" />
                <Column field="endpoint" header="Endpoint" :sortable="true" />
                <Column field="source" header="แหล่งที่มา" :sortable="true">
                    <template #body="slotProps">{{ sourceLabel(slotProps.data.source) }}</template>
                </Column>
                <Column field="calls" header="จำนวนครั้ง" :sortable="true" />
                <Column field="records" header="จำนวน records" :sortable="true" />
                <Column field="distinct_ips" header="จำนวน IP" :sortable="true" />
                <Column field="last_call" header="เรียกล่าสุด" :sortable="true" />
            </DataTable>
        </div>

        <div class="card p-mt-2">
            <h5>รายการเรียกใช้งาน (ล่าสุด {{ limit }} รายการ)</h5>
            <DataTable
                ref="detailTable"
                :value="rows"
                :loading="loading"
                :rowHover="true"
                :paginator="true"
                :rows="20"
                :rowsPerPageOptions="[20, 50, 100]"
                dataKey="id"
                exportFilename="api_access_logs"
                currentPageReportTemplate="แสดงผล {first} ถึง {last} จากทั้งหมด {totalRecords}"
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            >
                <template #empty>ไม่พบข้อมูล</template>
                <Column field="requested_at" header="เวลา" :sortable="true" />
                <Column field="source" header="แหล่งที่มา" :sortable="true">
                    <template #body="slotProps">{{ sourceLabel(slotProps.data.source) }}</template>
                </Column>
                <Column field="endpoint" header="Endpoint" :sortable="true" />
                <Column field="client_id" header="ผู้เรียก" :sortable="true" />
                <Column field="status_code" header="Status" :sortable="true" />
                <Column field="record_count" header="Records" :sortable="true" />
                <Column field="response_ms" header="ms" :sortable="true" />
                <Column field="ip_address" header="IP" :sortable="true" />
                <Column field="origin" header="Origin" :sortable="true" />
                <Column field="user_agent" header="User-Agent" />
                <Column header="Parameter" :exportable="false">
                    <template #body="slotProps">
                        <Button
                            icon="pi pi-align-center"
                            class="p-button-rounded p-button-info p-button-text"
                            :disabled="!slotProps.data.query_params"
                            @click="selectedParams = slotProps.data.query_params"
                        />
                    </template>
                </Column>
            </DataTable>
        </div>

        <Dialog header="Parameter" :visible="selectedParams !== null" :modal="true" @update:visible="selectedParams = null">
            <pre>{{ JSON.stringify(selectedParams, null, 2) }}</pre>
        </Dialog>
    </div>
</template>

<script>
import moment from 'moment';

const SOURCE_LABELS = { external: 'ระบบภายนอก', web: 'เว็บ MIS-GIS' };

export default {
    name: 'report-api-access-logs',
    data() {
        return {
            startTime: moment().subtract(7, 'days').startOf('day').toDate(),
            endTime: null,
            source: 'all',
            endpoint: '',
            sourceOptions: [
                { label: 'ทั้งหมด', value: 'all' },
                { label: SOURCE_LABELS.external, value: 'external' },
                { label: SOURCE_LABELS.web, value: 'web' }
            ],
            endpointOptions: [
                { label: 'ทั้งหมด', value: '' },
                { label: '/initial/firehydrant', value: '/initial/firehydrant' },
                { label: '/initial/extinguisher', value: '/initial/extinguisher' },
                { label: '/initial/consumables', value: '/initial/consumables' },
                { label: '/praram/firehydrant', value: '/praram/firehydrant' },
                { label: '/praram/extinguisher', value: '/praram/extinguisher' },
                { label: '/oauth/token (misgis-bkfire-api)', value: '/oauth/token' },
                { label: '/api/v1/firehydrants (misgis-bkfire-api)', value: '/api/v1/firehydrants' },
                { label: '/api/v1/extinguishers (misgis-bkfire-api)', value: '/api/v1/extinguishers' },
                { label: '/api/v1/consumables (misgis-bkfire-api)', value: '/api/v1/consumables' }
            ],
            summary: [],
            rows: [],
            limit: 1000,
            loading: false,
            selectedParams: null
        };
    },
    created() {
        this.loadLogs();
    },
    methods: {
        sourceLabel(source) {
            return SOURCE_LABELS[source] || source;
        },
        async loadLogs() {
            this.loading = true;
            try {
                const response = await this.axios.post(
                    `${this.$url_service}/get/api-access-logs`,
                    {
                        body: {
                            starttime: this.startTime ? moment(this.startTime).format('YYYY-MM-DD HH:mm:ss') : null,
                            endtime: this.endTime ? moment(this.endTime).format('YYYY-MM-DD HH:mm:ss') : null,
                            source: this.source,
                            endpoint: this.endpoint,
                            limit: this.limit
                        }
                    },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                );
                const data = response.data.response.data;
                this.summary = data.summary.map((row) => ({ ...row, key: `${row.day}|${row.endpoint}|${row.source}` }));
                this.rows = data.rows;
                this.limit = data.limit;
            } catch (error) {
                const status = error.response && error.response.status;
                const serverMessage = error.response && error.response.data && error.response.data.message;
                const detail =
                    status === 401 || status === 403
                        ? 'ไม่มีสิทธิ์ดูข้อมูลนี้ หรือ session หมดอายุ กรุณาเข้าสู่ระบบใหม่'
                        : `ไม่สามารถโหลดประวัติการเรียกใช้ API ได้${serverMessage ? ` (${serverMessage})` : ''}`;
                this.$toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาด', detail, life: 5000 });
            } finally {
                this.loading = false;
            }
        },
        clearFilters() {
            this.startTime = moment().subtract(7, 'days').startOf('day').toDate();
            this.endTime = null;
            this.source = 'all';
            this.endpoint = '';
            this.loadLogs();
        }
    }
};
</script>
